import React, { useEffect, useState } from "react";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { canManageContent } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Edit, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const EMPTY = {
  full_name: "", job_title: "", department: "", biography: "",
  qualifications: "", email: "", linkedin_url: "", photo_url: "",
  is_published: true, sort_order: 0,
};

export default function PortalTeam() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [editing, setEditing] = useState(null); // {} or member
  const canManage = canManageContent(user);

  const load = () => api.get("/team").then((r) => setTeam(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const onSave = async () => {
    try {
      if (editing.id) await api.patch(`/team/${editing.id}`, editing);
      else await api.post("/team", editing);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onDelete = async (m) => {
    if (!window.confirm(`Remove ${m.full_name}?`)) return;
    try { await api.delete(`/team/${m.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", "team");
    const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `/api/media/${data.storage_path}`;
  };

  return (
    <div className="p-6 lg:p-12" data-testid="portal-team">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-1">Studio</div>
          <h1 className="font-heading font-light text-3xl lg:text-4xl tracking-tighter">Team</h1>
        </div>
        {canManage && (
          <Button onClick={() => setEditing({ ...EMPTY })} data-testid="add-team-btn" className="bg-ink text-white hover:bg-ink/90 rounded-none">
            <Plus size={14} className="mr-2" /> Add team member
          </Button>
        )}
      </div>

      {team.length === 0 ? (
        <div className="border border-line bg-white p-14 text-center text-stone" data-testid="team-empty-portal">
          No team members yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {team.map((m) => (
            <div key={m.id} className="border border-line bg-white group" data-testid={`team-row-${m.id}`}>
              <div className="aspect-[4/5] bg-sand">
                {m.photo_url ? <img src={mediaUrl(m.photo_url)} alt={m.full_name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-stone text-xs uppercase tracking-widest">No photo</div>}
              </div>
              <div className="p-4">
                <div className="eyebrow text-stone mb-1">{m.department} {!m.is_published && "· Hidden"}</div>
                <div className="font-heading text-xl">{m.full_name}</div>
                <div className="text-sm text-stone">{m.job_title}</div>
                {canManage && (
                  <div className="flex justify-between items-center mt-4 pt-3 border-t border-line">
                    <button onClick={() => setEditing({ ...m })} className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5 hover:underline">
                      <Edit size={12} /> Edit
                    </button>
                    <button onClick={() => onDelete(m)} className="text-xs uppercase tracking-widest text-destructive hover:underline inline-flex items-center gap-1.5">
                      <Trash2 size={12} /> Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-light text-2xl">
              {editing?.id ? "Edit team member" : "New team member"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full name</Label>
                <Input value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} data-testid="team-name-input" />
              </div>
              <div>
                <Label>Job title</Label>
                <Input value={editing.job_title} onChange={(e) => setEditing({ ...editing, job_title: e.target.value })} />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })} />
              </div>
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={editing.sort_order || 0} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="md:col-span-2">
                <Label>Photo URL / upload</Label>
                {editing.photo_url && <img src={mediaUrl(editing.photo_url)} alt="" className="w-24 h-24 object-cover border border-line mb-2" />}
                <div className="flex gap-2">
                  <Input value={editing.photo_url || ""} onChange={(e) => setEditing({ ...editing, photo_url: e.target.value })} placeholder="Paste URL…" />
                  <label className="text-xs cursor-pointer border border-line px-3 py-2 hover:border-ink inline-flex items-center">
                    Upload
                    <input type="file" accept="image/*" hidden onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      try { const url = await uploadPhoto(f); setEditing((cur) => ({ ...cur, photo_url: url })); toast.success("Photo uploaded"); }
                      catch { toast.error("Upload failed"); }
                    }} />
                  </label>
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Biography</Label>
                <Textarea rows={3} value={editing.biography || ""} onChange={(e) => setEditing({ ...editing, biography: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Qualifications</Label>
                <Input value={editing.qualifications || ""} onChange={(e) => setEditing({ ...editing, qualifications: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </div>
              <div>
                <Label>LinkedIn URL</Label>
                <Input value={editing.linkedin_url || ""} onChange={(e) => setEditing({ ...editing, linkedin_url: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex items-center gap-3">
                <Switch checked={editing.is_published} onCheckedChange={(v) => setEditing({ ...editing, is_published: v })} />
                <Label>Publish on public website</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-ink text-white hover:bg-ink/90 rounded-none" onClick={onSave} data-testid="team-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
